import { Routes } from '@angular/router';
import {
  DEPOSIT_CRYPTO_METHODS,
  DEPOSIT_SKINS_METHODS,
  DEPOSIT_SURVEY_METHODS,
} from '@features/wallet/deposit/constants/deposit';
import { userGuard } from '@core/guards/user.guard';
import { adminGuard } from '@features/admin/guards/admin.guard';
import { adminEnvironmentGuard } from '@features/admin/guards/admin-environment.guard';
import { adminWithdrawGuard } from '@features/admin/guards/admin-witrhdraw.guard';
import { canDeactivateAutobet } from '@core/guards/can-deactivate-autobet.guard';
import { IS_SKINS_DEV_TOOLS_HIDDEN } from '@core/dev/dev-mock-skins';

import { HomeComponent } from '@features/pages/home/home.component';
import { AuthComponent } from '@features/pages/auth/auth.component';

const games: Routes = [
  {
    path: 'cases',
    loadComponent: () =>
      import('@features/games/cases/cases-list/cases-list.component').then(
        (m) => m.CasesListComponent
      ),
  },
  {
    path: 'cases/:link',
    loadComponent: () =>
      import('@features/games/cases/cases-open/cases-open.component').then(
        (m) => m.CasesOpenComponent
      ),
  },
  {
    path: 'battles',
    loadComponent: () =>
      import('@features/games/case-battles/cb-list/cb-list.component').then(
        (m) => m.CbListComponent
      ),
  },
  {
    path: 'battles/create',
    loadComponent: () =>
      import('@features/games/case-battles/cb-create/cb-create.component').then(
        (m) => m.CbCreateComponent
      ),
  },
  {
    path: 'battles/:gameuid',
    loadComponent: () =>
      import('@features/games/case-battles/cb-battle/cb-battle.component').then(
        (m) => m.CbBattleComponent
      ),
  },
  {
    path: 'king',
    loadComponent: () =>
      import('@features/games/king/king.component').then(
        (m) => m.KingComponent
      ),
  },
  {
    path: 'king-new',
    redirectTo: 'king',
  },
  {
    path: 'coinflip',
    loadComponent: () =>
      import('@features/games/coinflip/coinflip.component').then(
        (m) => m.CoinflipComponent
      ),
  },
  {
    path: 'coinflip/history',
    loadComponent: () =>
      import('@features/games/coinflip/coinflip-history/coinflip-history.component').then(
        (m) => m.CoinflipHistoryComponent
      ),
  },
  {
    path: 'plinko',
    loadComponent: () =>
      import('@features/games/plinko/plinko.component').then((m) => m.PlinkoComponent),
    canDeactivate: [canDeactivateAutobet],
  },
  {
    path: 'jackpot',
    loadComponent: () =>
      import(
        '@features/games/jackpot/jackpot-highroller/jackpot-highroller.component'
      ).then((m) => m.JackpotHighrollerComponent),
  },
  {
    path: 'upgrader',
    loadComponent: () =>
      import('@features/games/upgrader/upgrader.component').then(
        (m) => m.UpgraderComponent
      ),
  },
  {
    path: 'upgrader-new',
    redirectTo: 'upgrader',
  },
  {
    path: 'wheel',
    loadComponent: () =>
      import('@features/games/wheel/wheel/wheel.component').then((m) => m.WheelComponent),
  },
  {
    path: 'towers',
    loadComponent: () =>
      import('@features/games/towers/towers.component').then((m) => m.TowersComponent),
    canDeactivate: [canDeactivateAutobet],
  },
  {
    path: 'mines',
    loadComponent: () =>
      import('@features/games/mines/mines.component').then((m) => m.MinesComponent),
    canDeactivate: [canDeactivateAutobet],
  },
];

const admin: Routes = [
  {
    path: 'admin',
    loadComponent: () =>
      import('./features/admin/admin.component').then(
        (mod) => mod.AdminComponent
      ),
    canActivate: [adminGuard],
    children: [
      { path: '', redirectTo: 'users', pathMatch: 'full' },
      {
        path: 'new_dashboard',
        loadComponent: () =>
          import(
            './features/admin/admin-new-dashboard/admin-new-dashboard.component'
          ).then((mod) => mod.AdminNewDashboardComponent),
      },
      {
        path: 'old_dashboard',
        loadComponent: () =>
          import(
            './features/admin/admin-dashboard/admin-dashboard.component'
          ).then((mod) => mod.AdminDashboardComponent),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/admin/admin-users/admin-users.component').then(
            (mod) => mod.AdminUsersComponent
          ),
      },
      {
        path: 'bonus-results',
        loadComponent: () =>
          import(
            './features/admin/admin-bonus-results/admin-bonus-results.component'
          ).then((mod) => mod.AdminBonusResultsComponent),
      },
      {
        path: 'wager-tracker',
        loadComponent: () =>
          import(
            './features/admin/admin-wager-tracker/admin-wager-tracker.component'
          ).then((mod) => mod.AdminWagerTrackerComponent),
      },
      {
        path: 'affiliate-stats',
        loadComponent: () =>
          import(
            './features/admin/admin-affiliate-stats/admin-affiliate-stats.component'
          ).then((mod) => mod.AdminAffiliateStatsComponent),
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./features/admin/admin-support/admin-support.component').then(
            (mod) => mod.AdminSupportComponent
          ),
      },
      {
        path: 'items',
        loadComponent: () =>
          import('./features/admin/admin-items/admin-items.component').then(
            (mod) => mod.AdminItemsComponent
          ),
      },
      {
        path: 'unpriced',
        loadComponent: () =>
          import(
            './features/admin/admin-unpriced/admin-unpriced.component'
          ).then((mod) => mod.AdminUnpricedComponent),
      },
      {
        path: 'filter',
        loadComponent: () =>
          import('./features/admin/admin-filter/admin-filter.component').then(
            (mod) => mod.AdminFilterComponent
          ),
      },
      {
        path: 'limits',
        loadComponent: () =>
          import('./features/admin/admin-limits/admin-limits.component').then(
            (mod) => mod.AdminLimitsComponent
          ),
      },
      {
        path: 'games',
        loadComponent: () =>
          import('./features/admin/admin-games/admin-games.component').then(
            (mod) => mod.AdminGamesComponent
          ),
      },
      {
        path: 'trades',
        loadComponent: () =>
          import('./features/admin/admin-trades/admin-trades.component').then(
            (mod) => mod.AdminTradesComponent
          ),
      },
      {
        path: 'bots',
        loadComponent: () =>
          import('./features/admin/admin-bots/admin-bots.component').then(
            (mod) => mod.AdminBotsComponent
          ),
      },
      {
        path: 'case-creator',
        loadComponent: () =>
          import(
            './features/admin/admin-case-creator/admin-case-creator.component'
          ).then((mod) => mod.AdminCaseCreatorComponent),
      },
      {
        path: 'case-creator/case/:link',
        loadComponent: () =>
          import(
            './features/admin/admin-case-creator/admin-case-edit/admin-case-edit.component'
          ).then((mod) => mod.AdminCaseEditComponent),
      },
      {
        path: 'case-creator/new',
        loadComponent: () =>
          import(
            './features/admin/admin-case-creator/admin-case-edit/admin-case-edit.component'
          ).then((mod) => mod.AdminCaseEditComponent),
      },
      {
        path: 'withdraw',
        loadComponent: () =>
          import(
            './features/admin/admin-withdraw/admin-withdraw.component'
          ).then((mod) => mod.AdminWithdrawComponent),
        canActivate: [adminWithdrawGuard],
      },
      {
        path: 'environment',
        loadComponent: () =>
          import(
            './features/admin/admin-environment/admin-environment.component'
          ).then((mod) => mod.AdminEnvironmentComponent),
        canActivate: [adminEnvironmentGuard],
      },
      {
        path: 'new-pricing',
        loadComponent: () =>
          import(
            './features/admin/admin-new-pricing/admin-new-pricing.component'
          ).then((mod) => mod.AdminNewPricingComponent),
        loadChildren: () =>
          import(
            './features/admin/admin-new-pricing/admin-new-pricing.routes'
          ).then((mod) => mod.adminNewPricingRoutes),
      },
    ],
  },
];

/**
 * DEV ONLY — index of every screen that renders skins, for checking the grid
 * at real sizes without a Steam login.
 *
 * Gated on `IS_SKINS_DEV_TOOLS_HIDDEN`, which is a literal, so when it is
 * `true` the bundler folds this to `[]` and the dynamic import is dropped
 * entirely — no route, no chunk. Flip that constant to bring it back.
 */
const dev: Routes = IS_SKINS_DEV_TOOLS_HIDDEN
  ? []
  : [
      {
        path: 'dev/skins',
        loadComponent: () =>
          import(
            './features/dev/dev-skins-launcher/dev-skins-launcher.component'
          ).then((m) => m.DevSkinsLauncherComponent),
      },
    ];

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: HomeComponent,
  },
  {
    path: 'auth',
    component: AuthComponent,
  },
  // {
  //   path: 'faq',
  //   component: FaqComponent,
  // },
  // {
  //   path: 'tos',
  //   component: TosComponent,
  // },
  // {
  //   path: 'leaderboard',
  //   component: LeaderboardComponent,
  // },
  // {
  //   path: 'rewards',
  //   component: RewardsComponent,
  // },
  {
    path: 'leaderboard',
    loadComponent: () =>
      import('@features/engagement/leaderboard/leaderboard.component').then(
        (m) => m.LeaderboardComponent
      ),
  },
  {
    path: 'faq',
    loadComponent: () =>
      import('@features/pages/faq/faq.component').then((m) => m.FaqComponent),
  },
  {
    path: 'tos',
    loadComponent: () =>
      import('@features/pages/tos/tos.component').then((m) => m.TosComponent),
  },
  {
    path: 'rewards',
    loadComponent: () =>
      import('@features/engagement/rewards/rewards.component').then(
        (m) => m.RewardsComponent
      ),
  },
  {
    path: 'rewards/daily/:link',
    loadComponent: () =>
      import(
        '@features/engagement/rewards/rewards-daily-unboxing/rewards-daily-unboxing.component'
      ).then((m) => m.RewardsDailyUnboxingComponent),
  },
  {
    path: 'rewards/reward/:link',
    loadComponent: () =>
      import(
        '@features/engagement/rewards/rewards-reward-unboxing/rewards-reward-unboxing.component'
      ).then((m) => m.RewardsRewardUnboxingComponent),
  },
  {
    path: 'withdraw',
    // component: WithdrawComponent,
    loadComponent: () =>
      import('@features/wallet/withdraw/withdraw.component').then(
        (m) => m.WithdrawComponent
      ),
  },
  {
    path: 'provably-fair',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('@features/pages/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'provably-fair/:type',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('@features/pages/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'provably-fair/:type/:id',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('@features/pages/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'provably-fair/:type/:id/:roundid',
    // component: ProvablyFairComponent,
    loadComponent: () =>
      import('@features/pages/provably-fair/provably-fair.component').then(
        (m) => m.ProvablyFairComponent
      ),
  },
  {
    path: 'deposit',
    canActivate: [userGuard],
    // component: DepositComponent,
    loadComponent: () =>
      import('@features/wallet/deposit/deposit.component').then(
        (m) => m.DepositComponent
      ),
    children: [
      {
        path: '',
        // component: DepositMethodsListComponent,
        loadComponent: () =>
          import(
            '@features/wallet/deposit/deposit-methods-list/deposit-methods-list.component'
          ).then((m) => m.DepositMethodsListComponent),
      },
      ...DEPOSIT_CRYPTO_METHODS.methods.map((method) => {
        return {
          path: method.route,
          // component: DepositCryptoComponent,
          loadComponent: () =>
            import(
              '@features/wallet/deposit/deposit-crypto/deposit-crypto.component'
            ).then((m) => m.DepositCryptoComponent),
        };
      }),
      ...DEPOSIT_SURVEY_METHODS.methods.map((method) => {
        return {
          path: method.route,
          // component: DepositMethodsListComponent,
          loadComponent: () =>
            import(
              '@features/wallet/deposit/deposit-methods-list/deposit-methods-list.component'
            ).then((m) => m.DepositMethodsListComponent),
        };
      }),
      ...DEPOSIT_SKINS_METHODS.methods.map((method) => {
        return {
          path: method.route,
          // component: DepositMethodsListComponent,
          loadComponent: () =>
            import(
              '@features/wallet/deposit/deposit-methods-list/deposit-methods-list.component'
            ).then((m) => m.DepositMethodsListComponent),
        };
      }),
    ],
  },
  {
    path: 'support',
    canActivate: [userGuard],
    loadComponent: () =>
      import('@features/pages/support/support.component').then(
        (m) => m.SupportComponent
      ),
  },
  {
    path: 'raffles',
    loadComponent: () =>
      import('@features/engagement/giveaways/giveaways.component').then(
        (m) => m.GiveawaysComponent
      ),
  },
  {
    path: 'affiliates',
    loadComponent: () =>
      import(
        '@features/engagement/affiliates/affiliates-new/affiliates-new.component'
      ).then((m) => m.AffiliatesNewComponent),
  },
  {
    path: 'affiliates/cases/:link',
    // component: AffiliatesUnboxingComponent,
    loadComponent: () =>
      import(
        '@features/engagement/affiliates/affiliates-unboxing/affiliates-unboxing.component'
      ).then((m) => m.AffiliatesUnboxingComponent),
  },
  {
    path: 'r/:affCode',
    loadComponent: () =>
      import(
        '@features/engagement/affiliates/affiliates-new/affiliates-new.component'
      ).then((m) => m.AffiliatesNewComponent),
  },
  {
    path: 'p/:promoCode',
    loadComponent: () =>
      import('@features/pages/home/home.component').then((m) => m.HomeComponent),
  },
  ...games,
  ...admin,
  ...dev,
  {
    path: '**',
    redirectTo: 'jackpot',
  },
];
